import "../compliance/compliance.css";
import { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, InputNumber, Modal, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import debounce from "lodash/debounce";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { GetEmployeeRecord } from "@src/util/query/employee.query";
import { getAppraisalReviewersQuery, getAppraisalSettingsQuery } from "@src/util/query/appraisal.query";
import { newAppraisalMutation } from "@src/util/mutation/appraisal.mutation";
import { describeRepeat } from "./appraisalHelpers";

const HOW_OFTEN = [
    { key: "once", title: "Just this once", text: "A single appraisal." },
    { key: 6, title: "Every 6 months", text: "Repeats from the day it opens." },
    { key: 12, title: "Every year", text: "Repeats from the day it opens." },
    { key: "custom", title: "Custom", text: "Every few months." },
];

/**
 * Start an appraisal: who, which template, when it opens (today, or a later date - e.g. a new
 * starter's probation review), when it's due, whether it's one-off or repeats every N months
 * from the day it opens (never tied to the employee's start date), and who reviews it -
 * everyone with a role (shared until one takes it) or one person.
 */
export const StartAppraisalModal = ({ visible, employee = null, onClose, onStarted = () => {} }) => {
    const dispatch = useDispatch();
    const [employeeId, setEmployeeId] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [templateId, setTemplateId] = useState(null);
    const [title, setTitle] = useState("");
    const [opensOn, setOpensOn] = useState(dayjs().startOf("day"));
    const [dueDate, setDueDate] = useState(dayjs().add(14, "day"));
    const [howOften, setHowOften] = useState("once");
    const [customMonths, setCustomMonths] = useState(3);
    const [reviewBy, setReviewBy] = useState("role");
    const [reviewerRole, setReviewerRole] = useState(null);
    const [reviewerId, setReviewerId] = useState(null);

    const { data: settingsData } = useQuery(getAppraisalSettingsQuery, { skip: !visible, fetchPolicy: "network-only" });
    const templates = (settingsData?.getAppraisalSettings?.response?.templates || []).filter((t) => !t.isArchived);
    const roles = settingsData?.getAppraisalSettings?.response?.reviewerRoles || [];
    const [searchEmployees, { data: employeeData, loading: searching }] = useLazyQuery(GetEmployeeRecord);
    const { data: reviewersData } = useQuery(getAppraisalReviewersQuery, {
        variables: { employeeId, reviewerRole: reviewBy === "role" ? reviewerRole : null },
        skip: !visible || !employeeId,
        fetchPolicy: "network-only",
    });
    const reviewers = reviewersData?.getAppraisalReviewers?.response || [];
    const [start, { loading }] = useMutation(newAppraisalMutation);

    useEffect(() => {
        if (!visible) return;
        setEmployeeId(employee?._id || null);
        setEmployeeOptions(employee ? [{ value: employee._id, label: employee.name }] : []);
        setTemplateId(templates[0]?._id || null);
        setTitle("");
        setOpensOn(dayjs().startOf("day"));
        setDueDate(dayjs().add(14, "day"));
        setHowOften("once");
        setCustomMonths(3);
        setReviewBy("role");
        setReviewerId(null);
    }, [visible, employee?._id]);

    useEffect(() => {
        if (!templateId && templates.length) setTemplateId(templates[0]._id);
        if (!reviewerRole && roles.length) setReviewerRole(roles[0].value);
    }, [templates.length, roles.length]);

    useEffect(() => {
        const rows = employeeData?.getEmployee?.response || [];
        if (rows.length) setEmployeeOptions(rows.map((e) => ({ value: e._id, label: `${e.firstname || ""} ${e.lastname || ""}`.replace(/\s+/g, " ").trim() })));
    }, [employeeData]);

    const search = useMemo(() => debounce((value) => {
        if (value?.trim().length >= 2) searchEmployees({ variables: { input: { filters: null, search: value.trim(), page: 1, pageSize: 20 } }, fetchPolicy: "network-only" });
    }, 350), []);

    const suggested = reviewers.filter((r) => r.suggested);
    const repeatMonths = howOften === "once" ? null : howOften === "custom" ? customMonths : howOften;
    const opensToday = opensOn && opensOn.isSame(dayjs(), "day");

    // moving the open date keeps the same time to complete
    const changeOpensOn = (value) => {
        if (!value) return;
        const days = dueDate && opensOn ? dueDate.startOf("day").diff(opensOn.startOf("day"), "day") : 14;
        setOpensOn(value.startOf("day"));
        setDueDate(value.add(Math.max(days, 0), "day"));
    };
    const preview = () => {
        if (!opensOn || !dueDate) return "";
        const first = opensToday ? `Opens now, due ${dueDate.format("DD/MM/YYYY")}.` : `Opens on ${opensOn.format("DD/MM/YYYY")}, due ${dueDate.format("DD/MM/YYYY")} - nothing is sent until then.`;
        if (!repeatMonths) return first;
        return `${first} Then ${describeRepeat(repeatMonths).toLowerCase()} with the same form and reviewer - next opens ${opensOn.add(repeatMonths, "month").format("DD/MM/YYYY")}, due ${dueDate.add(repeatMonths, "month").format("DD/MM/YYYY")}.`;
    };
    const missing = [];
    if (!employeeId) missing.push("an employee");
    if (!templateId) missing.push("a template");
    if (!dueDate) missing.push("a due date");
    if (!opensOn) missing.push("when it opens");
    if (howOften === "custom" && !(customMonths >= 1)) missing.push("how many months");
    if (reviewBy === "person" && !reviewerId) missing.push("a reviewer");

    const save = async () => {
        try {
            const res = await start({
                variables: {
                    input: {
                        employeeId,
                        templateId,
                        title: title.trim() || undefined,
                        dueDate: dueDate.format("YYYY-MM-DD"),
                        opensOn: opensOn.format("YYYY-MM-DD"),
                        ...(repeatMonths ? { repeatEveryMonths: repeatMonths } : {}),
                        ...(reviewBy === "role" ? { reviewerRole } : { reviewerEmployeeId: reviewerId }),
                    },
                },
            });
            const response = res?.data?.newAppraisal?.response;
            const repeats = response?.everyMonths ? ` It repeats ${describeRepeat(response.everyMonths).toLowerCase()}.` : "";
            const message = !response?._id ? `Appraisal scheduled - it opens on ${dayjs(response?.opensOn).format("DD/MM/YYYY")}.${repeats}`
                : response.status === "awaitingOfficer" ? `Appraisal started - the officer is asked for their self-assessment first.${repeats}`
                    : `Appraisal started and sent to the reviewer.${repeats}`;
            dispatch(setNotification({ error: false, notificationState: true, message }));
            onStarted(res?.data?.newAppraisal?.response);
            onClose();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
        }
    };

    return (
        <Modal
            open={visible}
            width={620}
            closable={false}
            onCancel={onClose}
            footer={
                <div className="drawer-footer" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button className={loading || missing.length ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={loading || missing.length > 0} onClick={save}>
                        {loading ? <Spinner /> : opensToday ? "Start appraisal" : "Schedule appraisal"}
                    </button>
                    <button className={loading ? "disabled-btn drawer-outlined-btn" : "drawer-outlined-btn"} disabled={loading} onClick={onClose}>Cancel</button>
                    {missing.length > 0 && <span className="cmp-hint">Choose {missing.join(", ")}</span>}
                </div>
            }
        >
            <div className="modal-header-title">
                <span style={{ letterSpacing: "0.2px" }}>Start an appraisal</span>
                <span onClick={onClose}><FontAwesomeIcon className="close" icon={faClose} /></span>
            </div>
            <div className="modal-body apr-form" style={{ margin: "16px 0 0" }}>
                <div className="cmp-field">
                    <label>Employee</label>
                    <Select
                        className="custom-select"
                        showSearch
                        filterOption={false}
                        disabled={!!employee}
                        value={employeeId}
                        placeholder="Type a name to search"
                        notFoundContent={searching ? <Spinner /> : null}
                        onSearch={search}
                        onChange={(value) => { setEmployeeId(value); setReviewerId(null); }}
                        options={employeeOptions}
                    />
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Template</label>
                        <Select className="custom-select" value={templateId} onChange={setTemplateId} placeholder={templates.length ? "Choose" : "Create a template in Settings > Appraisals & Surveys"}
                            options={templates.map((t) => ({ value: t._id, label: t.name }))} />
                    </div>
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Title <span className="cmp-muted">(optional)</span></label>
                        <Input className="generic-input-control" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={templates.find((t) => t._id === templateId)?.name || "Uses the template's name"} />
                    </div>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Opens</label>
                        <DatePicker className="generic-input-control" format="DD/MM/YYYY" allowClear={false} value={opensOn} onChange={changeOpensOn} disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))} />
                    </div>
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Due</label>
                        <DatePicker className="generic-input-control" format="DD/MM/YYYY" allowClear={false} value={dueDate} onChange={setDueDate} disabledDate={(d) => d && opensOn && d.isBefore(opensOn.startOf("day"))} />
                    </div>
                </div>
                <div className="cmp-field">
                    <label>How often</label>
                    <div className="cmp-choice">
                        {HOW_OFTEN.map((option) => (
                            <button key={option.key} type="button" className={`cmp-choice-card${howOften === option.key ? " selected" : ""}`} onClick={() => setHowOften(option.key)}>
                                <div className="cmp-choice-card-title">{option.title}</div>
                                <div className="cmp-choice-card-text">{option.text}</div>
                            </button>
                        ))}
                    </div>
                    {howOften === "custom" && (
                        <InputNumber min={1} max={60} precision={0} size="large" style={{ width: 200 }} value={customMonths} onChange={(v) => setCustomMonths(v || 1)} addonBefore="Every" addonAfter="months" />
                    )}
                    <span className="cmp-hint">{preview()}</span>
                </div>
                <div className="cmp-field">
                    <label>Reviewed by</label>
                    <div className="cmp-choice">
                        <button type="button" className={`cmp-choice-card${reviewBy === "role" ? " selected" : ""}`} onClick={() => setReviewBy("role")}>
                            <div className="cmp-choice-card-title">Everyone with a role</div>
                            <div className="cmp-choice-card-text">Shared between them until one takes it.</div>
                        </button>
                        <button type="button" className={`cmp-choice-card${reviewBy === "person" ? " selected" : ""}`} onClick={() => setReviewBy("person")}>
                            <div className="cmp-choice-card-title">One person</div>
                            <div className="cmp-choice-card-text">Goes straight to them.</div>
                        </button>
                    </div>
                </div>
                {reviewBy === "role" ? (
                    <div className="cmp-field">
                        <Select className="custom-select" value={reviewerRole} onChange={setReviewerRole} options={roles} />
                        {employeeId && (
                            <span className="cmp-hint">
                                {suggested.length === 0 ? "Nobody with this role can review this employee - choose another role or one person."
                                    : suggested.length === 1 ? `Goes to ${suggested[0].name}.`
                                        : `Shared between ${suggested.length}: ${suggested.slice(0, 4).map((r) => r.name).join(", ")}${suggested.length > 4 ? "..." : ""}.`}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="cmp-field">
                        <Select className="custom-select" showSearch optionFilterProp="label" value={reviewerId} onChange={setReviewerId} disabled={!employeeId}
                            placeholder={employeeId ? "Choose someone with a WFC360 login" : "Choose the employee first"}
                            options={reviewers.map((r) => ({ value: r._id, label: r.name }))} />
                    </div>
                )}
            </div>
        </Modal>
    );
};
